/**
 * FN-FE-01 — Design System Showcase (/ui-kit alias: /_design-system)
 */
import { useState } from 'react';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Textarea from '../../components/ui/Textarea';
import DatePicker from '../../components/ui/DatePicker';
import Card from '../../components/ui/Card';
import Modal from '../../components/ui/Modal';
import Badge from '../../components/ui/Badge';
import EmptyState from '../../components/ui/EmptyState';
import ErrorState from '../../components/ui/ErrorState';
import Skeleton from '../../components/ui/Skeleton';
import PageHeader from '../../components/ui/PageHeader';
import Tabs from '../../components/ui/Tabs';
import Table from '../../components/ui/Table';
import Tooltip from '../../components/ui/Tooltip';
import Pagination from '../../components/ui/Pagination';
import { useToast, ToastProvider } from '../../components/ui/Toast';

const S = ({ title }: { title: string }) => (
  <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: '20px', fontWeight: 800, marginBottom: '16px', paddingBottom: '8px', borderBottom: '2px solid var(--line)' }}>{title}</h2>
);

const DEMO_ROWS = [
  { id: '1', name: 'Modulum Kft.', status: 'Aktív', amount: '1 200 000 Ft' },
  { id: '2', name: 'TechCorp Zrt.', status: 'Próba', amount: '340 000 Ft' },
  { id: '3', name: 'Demo Partner Bt.', status: 'Lejárt', amount: '0 Ft' },
];

export default function DesignSystemShowcase() {
  return (
    <ToastProvider>
      <ShowcaseInner />
    </ToastProvider>
  );
}

function ShowcaseInner() {
  const [modalOpen, setModalOpen] = useState(false);
  const [page, setPage] = useState(3);
  const { toast } = useToast();

  return (
    <div style={{ maxWidth: '960px', margin: '0 auto', padding: '32px 24px', fontFamily: 'var(--font-body)' }}>
      <h1 style={{ fontFamily: 'var(--font-heading)', fontSize: '28px', fontWeight: 800, marginBottom: '8px' }}>Modulum Design System</h1>
      <p style={{ color: 'var(--slate)', marginBottom: '48px' }}>Elérhető: <code>/_design-system</code> vagy <code>/ui-kit</code></p>

      {/* ── Spacing tokens ── */}
      <section style={{ marginBottom: '48px' }}>
        <S title="Spacing tokens" />
        <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
          {[1,2,3,4,5,6,8,10,12].map(n => (
            <div key={n} style={{ textAlign: 'center' }}>
              <div style={{ background: 'var(--orange)', width: `var(--space-${n})`, height: `var(--space-${n})` }} />
              <p style={{ fontSize: '10px', marginTop: '4px', color: 'var(--slate)' }}>--space-{n}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Colours ── */}
      <section style={{ marginBottom: '48px' }}>
        <S title="Színek" />
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
          {['#131C32','#1F2A4D','#28365E','#3D4D7A','#5A6997','#8B96BC','#B8C1D7'].map(c => (
            <div key={c} style={{ width: '48px', height: '48px', borderRadius: '8px', background: c }} title={c} />
          ))}
          <div style={{ width: '12px' }} />
          {['#B0500A','#DB6510','#F47B20','#F88A3C','#FFA866','#FFC899','#FFE3CC'].map(c => (
            <div key={c} style={{ width: '48px', height: '48px', borderRadius: '8px', background: c }} title={c} />
          ))}
          <div style={{ width: '12px' }} />
          {['#16A34A','#EA580C','#DC2626','#2563EB'].map(c => (
            <div key={c} style={{ width: '48px', height: '48px', borderRadius: '8px', border: '1px solid var(--line)', background: c }} title={c} />
          ))}
        </div>
      </section>

      {/* ── Typography ── */}
      <section style={{ marginBottom: '48px' }}>
        <S title="Tipográfia" />
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', fontFamily: 'var(--font-heading)' }}>
          {[['36px', '800', 'Display / H1'], ['28px', '800', 'H2'], ['22px', '700', 'H3'], ['18px', '700', 'H4'], ['15px', '600', 'H5']].map(([size, weight, label]) => (
            <p key={label} style={{ fontSize: size, fontWeight: weight as unknown as number }}>{label} — Plus Jakarta Sans</p>
          ))}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '16px', fontFamily: 'var(--font-body)' }}>
          <p style={{ fontSize: '16px' }}>Body Base (16px) — Hanken Grotesk</p>
          <p style={{ fontSize: '14px' }}>Body MD (14px)</p>
          <p style={{ fontSize: '13px' }}>Body SM (13px) — alap méret</p>
          <p style={{ fontSize: '12px', color: 'var(--slate)' }}>Caption (12px)</p>
          <p style={{ fontSize: '11px', color: 'var(--slate-2)', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700 }}>LABEL / UPPERCASE (11px)</p>
        </div>
      </section>

      {/* ── Buttons ── */}
      <section style={{ marginBottom: '48px' }}>
        <S title="Gombok" />
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '12px' }}>
          <Button variant="primary">Primary</Button>
          <Button variant="secondary">Secondary</Button>
          <Button variant="ghost">Ghost</Button>
          <Button variant="danger">Danger</Button>
          <Button variant="success">Success</Button>
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '12px' }}>
          <Button size="sm">Small</Button>
          <Button size="md">Medium</Button>
          <Button size="lg">Large</Button>
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
          <Button loading>Betöltés</Button>
          <Button disabled>Letiltva</Button>
          <Button icon={<span>+</span>}>Ikonnal</Button>
          <Button icon={<span>→</span>} iconPosition="right">Jobbra</Button>
        </div>
      </section>

      {/* ── Form elements ── */}
      <section style={{ marginBottom: '48px' }}>
        <S title="Form elemek" />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', maxWidth: '640px' }}>
          <Input label="Cégnév" placeholder="Pl. Modulum Kft." helperText="A NAV-nál regisztrált cégnév." />
          <Input label="Adószám" placeholder="12345678-2-43" error="Hibás adószám." />
          <Input label="E-mail" type="email" placeholder="info@ceg.hu" />
          <Input label="Összeg" type="number" placeholder="0" disabled />
          <Select
            label="Státusz"
            placeholder="Válassz..."
            options={[{ value: 'active', label: 'Aktív' }, { value: 'trial', label: 'Próbaidő' }, { value: 'cancelled', label: 'Lemondva' }]}
            helperText="Az előfizetés aktuális állapota."
          />
          <Select
            label="Hiba Select"
            placeholder="Válassz..."
            options={[{ value: 'a', label: 'Opció A' }]}
            error="Kötelező mező."
          />
          <DatePicker label="Dátum" />
          <DatePicker label="Lejárat (hiba)" error="Érvénytelen dátum." />
          <div style={{ gridColumn: '1 / -1' }}>
            <Textarea label="Megjegyzés" placeholder="Ide írj megjegyzést..." helperText="Max 500 karakter." />
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <Textarea label="Hiba Textarea" error="Ez a mező kötelező." />
          </div>
        </div>
      </section>

      {/* ── Badges ── */}
      <section style={{ marginBottom: '48px' }}>
        <S title="Badge-ek" />
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <Badge>Default</Badge>
          <Badge variant="success">Aktív</Badge>
          <Badge variant="warning">Próba</Badge>
          <Badge variant="error">Lejárt</Badge>
          <Badge variant="info">Új</Badge>
        </div>
      </section>

      {/* ── Tooltip ── */}
      <section style={{ marginBottom: '48px' }}>
        <S title="Tooltip" />
        <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
          <Tooltip content="Felső tooltip" position="top"><Button variant="ghost" size="sm">Felső</Button></Tooltip>
          <Tooltip content="Alsó tooltip" position="bottom"><Button variant="ghost" size="sm">Alsó</Button></Tooltip>
          <Tooltip content="Bal oldali tooltip" position="left"><Button variant="ghost" size="sm">Bal</Button></Tooltip>
          <Tooltip content="Jobb oldali tooltip" position="right"><Button variant="ghost" size="sm">Jobb</Button></Tooltip>
        </div>
      </section>

      {/* ── Card ── */}
      <section style={{ marginBottom: '48px' }}>
        <S title="Kártya" />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', maxWidth: '640px' }}>
          <Card>
            <Card.Header><h3 style={{ fontWeight: 700 }}>Normál kártya</h3></Card.Header>
            <Card.Body><p style={{ fontSize: '13px', color: 'var(--slate)' }}>Tartalmi rész. Ide jön az adat.</p></Card.Body>
            <Card.Footer><Button size="sm">Mentés</Button></Card.Footer>
          </Card>
          <Card onClick={() => toast('Kártyára kattintottál', 'success')}>
            <Card.Header><h3 style={{ fontWeight: 700 }}>Kattintható kártya</h3></Card.Header>
            <Card.Body><p style={{ fontSize: '13px', color: 'var(--slate)' }}>Pointer kurzor, hover állapot.</p></Card.Body>
          </Card>
        </div>
      </section>

      {/* ── Toast ── */}
      <section style={{ marginBottom: '48px' }}>
        <S title="Toast értesítések" />
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <Button variant="ghost" size="sm" onClick={() => toast('Sikeres mentés!', 'success')}>Success toast</Button>
          <Button variant="ghost" size="sm" onClick={() => toast('Hiba történt.', 'error')}>Error toast</Button>
          <Button variant="ghost" size="sm" onClick={() => toast('Figyelem: próba időszak.', 'warning')}>Warning toast</Button>
          <Button variant="ghost" size="sm" onClick={() => toast('Adatok betöltve.', 'info')}>Info toast</Button>
        </div>
      </section>

      {/* ── Table ── */}
      <section style={{ marginBottom: '48px' }}>
        <S title="Táblázat" />
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <Table
            columns={[
              { key: 'name', label: 'Név' },
              { key: 'status', label: 'Státusz', render: (r) => <Badge variant={r.status === 'Aktív' ? 'success' : r.status === 'Próba' ? 'warning' : 'error'}>{r.status}</Badge> },
              { key: 'amount', label: 'Összeg' },
            ]}
            rows={DEMO_ROWS}
            rowKey={r => r.id}
          />
          <p style={{ fontSize: '12px', color: 'var(--slate)' }}>Loading állapot:</p>
          <Table columns={[{ key: 'name', label: 'Név' }, { key: 'status', label: 'Státusz' }]} rows={[]} rowKey={r => r} loading />
          <p style={{ fontSize: '12px', color: 'var(--slate)' }}>Üres állapot:</p>
          <Table columns={[{ key: 'name', label: 'Név' }, { key: 'status', label: 'Státusz' }]} rows={[]} rowKey={r => r} emptyTitle="Nincs rekord" emptyDescription="Hozz létre egyet a + Új gombbal." />
        </div>
      </section>

      {/* ── Modal ── */}
      <section style={{ marginBottom: '48px' }}>
        <S title="Modal" />
        <Button onClick={() => setModalOpen(true)}>Modal megnyitása</Button>
        <Modal
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          title="Megerősítés"
          footer={
            <>
              <Button variant="ghost" onClick={() => setModalOpen(false)}>Mégse</Button>
              <Button onClick={() => { setModalOpen(false); toast('Mentve!', 'success'); }}>Mentés</Button>
            </>
          }
        >
          <p style={{ fontSize: '13px', color: 'var(--slate)' }}>Biztos vagy benne, hogy mented a változtatásokat?</p>
        </Modal>
      </section>

      {/* ── Tabs ── */}
      <section style={{ marginBottom: '48px' }}>
        <S title="Tab-ek" />
        <Tabs tabs={[
          { label: 'Általános', content: <p style={{ fontSize: '13px', color: 'var(--slate)', padding: '8px 0' }}>Általános tartalom itt.</p> },
          { label: 'Részletek', content: <p style={{ fontSize: '13px', color: 'var(--slate)', padding: '8px 0' }}>Részletes tartalom.</p> },
          { label: 'Történet', content: <p style={{ fontSize: '13px', color: 'var(--slate)', padding: '8px 0' }}>Történeti adatok.</p> },
        ]} />
      </section>

      {/* ── Pagination ── */}
      <section style={{ marginBottom: '48px' }}>
        <S title="Lapozás" />
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <Pagination page={page} totalPages={10} onPageChange={setPage} />
          <p style={{ fontSize: '12px', color: 'var(--slate)' }}>Aktuális oldal: {page}</p>
          <Pagination page={1} totalPages={3} onPageChange={() => {}} />
          <Pagination page={1} totalPages={1} onPageChange={() => {}} />
        </div>
      </section>

      {/* ── Empty + Error states ── */}
      <section style={{ marginBottom: '48px' }}>
        <S title="Üres és hibás állapot" />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          <Card>
            <EmptyState icon="📋" title="Nincsenek partnerek" description="Hozz létre egyet a + Új partner gombbal." action={<Button size="sm">+ Új partner</Button>} />
          </Card>
          <Card>
            <ErrorState title="Betöltési hiba" description="Az adatok betöltése sikertelen. Ellenőrizd a kapcsolatot." onRetry={() => toast('Újrapróbálva', 'info')} />
          </Card>
        </div>
      </section>

      {/* ── Skeleton ── */}
      <section style={{ marginBottom: '48px' }}>
        <S title="Skeleton loading" />
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', maxWidth: '400px' }}>
          <Skeleton className="h-8 w-3/4" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-5/6" />
          <Skeleton className="h-24 w-full" />
        </div>
      </section>

      {/* ── PageHeader ── */}
      <section style={{ marginBottom: '48px' }}>
        <S title="Page Header" />
        <PageHeader title="Partnerek" description="Vevők és beszállítók kezelése." actions={<Button size="sm">+ Új partner</Button>} />
      </section>
    </div>
  );
}
